import type { CSSProperties } from "react";
import { usePuckTheme } from "@/context/cms-puck/PuckThemeContext";
import {
  normalizeBlockStyle,
  resolveBlockStyleCss,
  resolveBlockTextCss,
  type BlockStyle,
} from "@/lib/cms-puck/block-style";
import { useGetAttachmentById, type Attachment } from "@/api/attachment";

/**
 * useBlockStyleCss — résout un `BlockStyle` en CSS inline depuis la palette du
 * thème courant (Provider monté autour de `<Puck>`). Source UNIQUE de résolution :
 *  - `StyleWrapper` l'utilise pour les blocs « section » (style → wrapper) ;
 *  - les widgets ATOMIQUES (Bouton, Icône) l'appellent dans leur propre render
 *    pour poser le style SUR l'élément lui-même (le fond doit battre la classe de
 *    variante → style inline sur le `<span>`, pas un wrapper pleine largeur).
 *
 * KEEP IN SYNC avec inventozen-frontpublic (hook équivalent sur PuckPaletteContext).
 */
export function useBlockStyleCss(
  style: BlockStyle | undefined,
): CSSProperties {
  const theme = usePuckTheme();
  const palette = theme?.colors ?? [];
  return resolveBlockStyleCss(palette, normalizeBlockStyle(style)) as CSSProperties;
}

/**
 * useBlockStyleCssWithImage — comme `useBlockStyleCss`, MAIS résout aussi l'image
 * de fond (`design.backgroundImage.attachmentId`) en URL via react-query
 * (`useGetAttachmentById`, le canvas BO n'a pas de refs context) et la passe à
 * `resolveBlockStyleCss`. Utilisé par `StyleWrapper` (blocs « section »/wrapper).
 * Les widgets atomiques (Bouton/Icône) gardent `useBlockStyleCss` (pas d'image).
 * KEEP IN SYNC avec le front (StyleWrapper → usePuckAttachment).
 */
export function useBlockStyleCssWithImage(
  style: BlockStyle | undefined,
): CSSProperties {
  const theme = usePuckTheme();
  const palette = theme?.colors ?? [];
  const normalized = normalizeBlockStyle(style);
  const attachmentId = normalized.design.backgroundImage.attachmentId ?? "";
  // `enabled:!!id` côté hook → aucun fetch quand pas d'image de fond.
  const { data } = useGetAttachmentById(attachmentId);
  const att = attachmentId ? (data as Attachment | undefined) : undefined;
  const backgroundImageUrl = att
    ? att.largePublicUrl ?? att.mediumPublicUrl ?? att.publicUrl
    : undefined;
  return resolveBlockStyleCss(palette, normalized, {
    backgroundImageUrl,
  }) as CSSProperties;
}

/**
 * useBlockTextCss — sous-ensemble TEXTE du style (couleur/police/graisse/taille/
 * alignement/casse/italique/décoration), à appliquer EN INLINE SUR L'ÉLÉMENT de
 * texte lui-même (ex. le `<h1>` du Titre) pour surcharger ses classes Tailwind.
 * KEEP IN SYNC avec inventozen-frontpublic.
 */
export function useBlockTextCss(
  style: BlockStyle | undefined,
): CSSProperties {
  const theme = usePuckTheme();
  const palette = theme?.colors ?? [];
  return resolveBlockTextCss(palette, normalizeBlockStyle(style)) as CSSProperties;
}
